// ReinsuranceContactStep.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Grid, Typography, Box, FormControlLabel, Checkbox, MenuItem } from '@mui/material';
import STATES from '../constants/states';
import { isValidEmail, isValidZipCode, isValidPhone } from '../utils/validators';
import PhoneMaskCustom from '../components/PhoneMaskCustom';
import { SignupData } from 'form-application-shared';

interface ReinsuranceContactStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
}

interface ReinsuranceContactErrors {
  reinsuranceContactName?: string;
  reinsuranceContactRelation?: string;
  reinsuranceContactEmail?: string;
  reinsuranceContactPhone?: string;
  reinsuranceContactAccountingEmail?: string;
  reinsuranceContactPhysicalAddressLines?: string;
  reinsuranceContactPhysicalCity?: string;
  reinsuranceContactPhysicalState?: string;
  reinsuranceContactPhysicalCounty?: string;
  reinsuranceContactPhysicalZip?: string;
  reinsuranceContactMailingAddressLines?: string;
  reinsuranceContactMailingCity?: string;
  reinsuranceContactMailingState?: string;
  reinsuranceContactMailingZip?: string;
}

const ReinsuranceContactStep: React.FC<ReinsuranceContactStepProps> = ({ 
  formData, 
  setFormData, 
  onValidationChange 
}) => {
  const [errors, setErrors] = useState<ReinsuranceContactErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validation function
  const validateStep = useCallback(() => {
    let isValid = true;
    const newErrors: ReinsuranceContactErrors = {};

    // Required fields validation
    if (!formData.reinsuranceContactName) {
      newErrors.reinsuranceContactName = 'Contact name is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactRelation) {
      newErrors.reinsuranceContactRelation = 'Contact relation is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactEmail) {
      newErrors.reinsuranceContactEmail = 'Contact email is required';
      isValid = false;
    } else if (!isValidEmail(formData.reinsuranceContactEmail)) {
      newErrors.reinsuranceContactEmail = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!formData.reinsuranceContactPhone) {
      newErrors.reinsuranceContactPhone = 'Contact phone is required';
      isValid = false;
    } else if (!isValidPhone(formData.reinsuranceContactPhone)) {
      newErrors.reinsuranceContactPhone = 'Please enter a valid phone number';
      isValid = false;
    }

    // Accounting email validation
    if (!formData.reinsuranceContactAccountingEmail) {
      newErrors.reinsuranceContactAccountingEmail = 'Accounting email is required';
      isValid = false;
    } else if (!isValidEmail(formData.reinsuranceContactAccountingEmail)) {
      newErrors.reinsuranceContactAccountingEmail = 'Please enter a valid email address';
      isValid = false;
    }

    // Physical address validation
    if (!formData.reinsuranceContactPhysicalAddressLines) {
      newErrors.reinsuranceContactPhysicalAddressLines = 'Physical address is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactPhysicalCity) {
      newErrors.reinsuranceContactPhysicalCity = 'City is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactPhysicalState) {
      newErrors.reinsuranceContactPhysicalState = 'State is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactPhysicalCounty) {
      newErrors.reinsuranceContactPhysicalCounty = 'County is required';
      isValid = false;
    }

    if (!formData.reinsuranceContactPhysicalZip) {
      newErrors.reinsuranceContactPhysicalZip = 'ZIP code is required';
      isValid = false;
    } else if (!isValidZipCode(formData.reinsuranceContactPhysicalZip)) {
      newErrors.reinsuranceContactPhysicalZip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
      isValid = false;
    }

    // Mailing address validation (only if enabled)
    if (formData.reinsuranceContactMailingEnabled) {
      if (!formData.reinsuranceContactMailingAddressLines) {
        newErrors.reinsuranceContactMailingAddressLines = 'Mailing address is required';
        isValid = false;
      }

      if (!formData.reinsuranceContactMailingCity) {
        newErrors.reinsuranceContactMailingCity = 'City is required';
        isValid = false;
      }

      if (!formData.reinsuranceContactMailingState) {
        newErrors.reinsuranceContactMailingState = 'State is required';
        isValid = false;
      }

      if (!formData.reinsuranceContactMailingZip) {
        newErrors.reinsuranceContactMailingZip = 'ZIP code is required';
        isValid = false;
      } else if (!isValidZipCode(formData.reinsuranceContactMailingZip)) {
        newErrors.reinsuranceContactMailingZip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [
    formData.reinsuranceContactName,
    formData.reinsuranceContactRelation,
    formData.reinsuranceContactEmail,
    formData.reinsuranceContactPhone,
    formData.reinsuranceContactAccountingEmail,
    formData.reinsuranceContactPhysicalAddressLines,
    formData.reinsuranceContactPhysicalCity,
    formData.reinsuranceContactPhysicalState,
    formData.reinsuranceContactPhysicalCounty,
    formData.reinsuranceContactPhysicalZip,
    formData.reinsuranceContactMailingEnabled,
    formData.reinsuranceContactMailingAddressLines,
    formData.reinsuranceContactMailingCity,
    formData.reinsuranceContactMailingState,
    formData.reinsuranceContactMailingZip
  ]);

  // Add a function to mark all fields as touched - wrapped in useCallback
  const markAllFieldsAsTouched = useCallback(() => {
    const allFields: Record<string, boolean> = {
      reinsuranceContactName: true,
      reinsuranceContactRelation: true,
      reinsuranceContactEmail: true,
      reinsuranceContactPhone: true,
      reinsuranceContactAccountingEmail: true,
      reinsuranceContactPhysicalAddressLines: true,
      reinsuranceContactPhysicalCity: true,
      reinsuranceContactPhysicalState: true,
      reinsuranceContactPhysicalCounty: true,
      reinsuranceContactPhysicalZip: true,
    };
    
    // Add mailing fields if enabled
    if (formData.reinsuranceContactMailingEnabled) {
      allFields.reinsuranceContactMailingAddressLines = true;
      allFields.reinsuranceContactMailingCity = true;
      allFields.reinsuranceContactMailingState = true;
      allFields.reinsuranceContactMailingZip = true;
    }
    
    setTouched(allFields);
  }, [formData.reinsuranceContactMailingEnabled]); // Include dependency on mailing enabled state

  // Create a validation wrapper that marks all fields as touched when validating
  const validateStepAndMarkTouched = useCallback(() => {
    markAllFieldsAsTouched();
    return validateStep();
  }, [validateStep, markAllFieldsAsTouched]); // Include markAllFieldsAsTouched in dependencies

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateStepAndMarkTouched);
    }
  }, [onValidationChange, validateStepAndMarkTouched]);

  // Validate on each change to provide real-time feedback,
  // but only after fields have been touched
  useEffect(() => {
    validateStep();
  }, [validateStep]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Mark field as touched when changed
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Reinsurance Contact Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Contact Name"
            name="reinsuranceContactName"
            value={formData.reinsuranceContactName || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactName && !!errors.reinsuranceContactName}
            helperText={touched.reinsuranceContactName && errors.reinsuranceContactName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Contact Relation"
            name="reinsuranceContactRelation"
            value={formData.reinsuranceContactRelation || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactRelation && !!errors.reinsuranceContactRelation}
            helperText={touched.reinsuranceContactRelation && errors.reinsuranceContactRelation}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Email"
            name="reinsuranceContactEmail"
            type="email"
            value={formData.reinsuranceContactEmail || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactEmail && !!errors.reinsuranceContactEmail}
            helperText={touched.reinsuranceContactEmail && errors.reinsuranceContactEmail}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Phone"
            name="reinsuranceContactPhone"
            value={formData.reinsuranceContactPhone || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhone && !!errors.reinsuranceContactPhone}
            helperText={touched.reinsuranceContactPhone && errors.reinsuranceContactPhone}
            InputProps={{
              inputComponent: PhoneMaskCustom as any,
            }}
          />
        </Grid>
        
        {/* Accounting Email field */}
        <Grid item xs={12}>
          <TextField
            label="Accounting Email (Recieives Financial Statements & Cessions)"
            name="reinsuranceContactAccountingEmail"
            type="email"
            value={formData.reinsuranceContactAccountingEmail || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactAccountingEmail && !!errors.reinsuranceContactAccountingEmail}
            helperText={
              touched.reinsuranceContactAccountingEmail && errors.reinsuranceContactAccountingEmail
                ? errors.reinsuranceContactAccountingEmail
                : "Please include email address where you would like financial statements & cessions delivered to."
            }
          />
        </Grid>

        {/* Physical Address */}
        <Grid item xs={12}>
          <Typography variant="h6">Physical Address</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Address"
            name="reinsuranceContactPhysicalAddressLines"
            value={formData.reinsuranceContactPhysicalAddressLines || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhysicalAddressLines && !!errors.reinsuranceContactPhysicalAddressLines}
            helperText={touched.reinsuranceContactPhysicalAddressLines && errors.reinsuranceContactPhysicalAddressLines}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="City"
            name="reinsuranceContactPhysicalCity"
            value={formData.reinsuranceContactPhysicalCity || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhysicalCity && !!errors.reinsuranceContactPhysicalCity}
            helperText={touched.reinsuranceContactPhysicalCity && errors.reinsuranceContactPhysicalCity}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            label="State"
            name="reinsuranceContactPhysicalState"
            value={formData.reinsuranceContactPhysicalState || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhysicalState && !!errors.reinsuranceContactPhysicalState}
            helperText={touched.reinsuranceContactPhysicalState && errors.reinsuranceContactPhysicalState}
          >
            {STATES.map(({ code, name }) => (
              <MenuItem key={code} value={code}>
                {code} - {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="County"
            name="reinsuranceContactPhysicalCounty"
            value={formData.reinsuranceContactPhysicalCounty || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhysicalCounty && !!errors.reinsuranceContactPhysicalCounty}
            helperText={touched.reinsuranceContactPhysicalCounty && errors.reinsuranceContactPhysicalCounty}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Zip"
            name="reinsuranceContactPhysicalZip"
            value={formData.reinsuranceContactPhysicalZip || ""}
            onChange={handleChange}
            fullWidth
            required
            error={touched.reinsuranceContactPhysicalZip && !!errors.reinsuranceContactPhysicalZip}
            helperText={touched.reinsuranceContactPhysicalZip && errors.reinsuranceContactPhysicalZip}
          />
        </Grid>

        {/* Mailing Address */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="reinsuranceContactMailingEnabled"
                checked={formData.reinsuranceContactMailingEnabled}
                onChange={handleChange}
              />
            }
            label="Mailing address is different from physical address"
          />
        </Grid>
        {formData.reinsuranceContactMailingEnabled && (
          <>
            <Grid item xs={12}>
              <TextField
                label="Mailing Address"
                name="reinsuranceContactMailingAddressLines"
                value={formData.reinsuranceContactMailingAddressLines || ""}
                onChange={handleChange}
                fullWidth
                required
                error={touched.reinsuranceContactMailingAddressLines && !!errors.reinsuranceContactMailingAddressLines}
                helperText={touched.reinsuranceContactMailingAddressLines && errors.reinsuranceContactMailingAddressLines}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                name="reinsuranceContactMailingCity"
                value={formData.reinsuranceContactMailingCity || ""}
                onChange={handleChange}
                fullWidth
                required
                error={touched.reinsuranceContactMailingCity && !!errors.reinsuranceContactMailingCity}
                helperText={touched.reinsuranceContactMailingCity && errors.reinsuranceContactMailingCity}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="State"
                name="reinsuranceContactMailingState"
                value={formData.reinsuranceContactMailingState || ""}
                onChange={handleChange}
                fullWidth
                required
                error={touched.reinsuranceContactMailingState && !!errors.reinsuranceContactMailingState}
                helperText={touched.reinsuranceContactMailingState && errors.reinsuranceContactMailingState}
              >
                {STATES.map(({ code, name }) => (
                  <MenuItem key={code} value={code}>
                    {code} - {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Zip"
                name="reinsuranceContactMailingZip"
                value={formData.reinsuranceContactMailingZip || ""}
                onChange={handleChange}
                fullWidth
                required
                error={touched.reinsuranceContactMailingZip && !!errors.reinsuranceContactMailingZip}
                helperText={touched.reinsuranceContactMailingZip && errors.reinsuranceContactMailingZip}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default ReinsuranceContactStep;